import {
  Dialog,
  DialogContent,
  DialogTitle,
  useTheme,
  Typography,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { FC, useMemo, useState } from "react";
import { gql } from "@shared/utils";
import {useMutation} from '@apollo/client/react';
import { CategoryData } from "../ProductOption";
import { GroupedOptionListItem } from "../../../utils/group";
import { IcIcon } from "@shared/components";
import { useProductOptionContext } from "../ProductOptionContext";

export const REMOVE_PRODUCT_CONFIGURATION_LIST_ITEM = gql`
  mutation RemoveProductConfigurationListItem(
    $subCategoryIds: [String!]
    $productConfigurationId: String!
  ) {
    removeProductConfigurationListItem(
      input: { id: $productConfigurationId, subCategoryIds: $subCategoryIds }
    ) {
      _id
      OptionsList {
        SubCategory {
          name
          maxOptions
          _id
          Category {
            name
            _id
          }
          Options {
            name
            _id
            AllowList {
              subcategory
              options
            }
          }
        }
        options
      }
    }
  }
`;

const expandCategories = (
  categoryIds: string[],
  categorys: CategoryData[]
): string[] => {
  const result = [...(categoryIds || [])];
  for (const categoryId of categoryIds) {
    const cat = categorys.find((c) => c._id === categoryId);
    if (cat && cat.requiredCategorys) {
      result.push(...cat.requiredCategorys);
    }
  }
  return result.filter((id, index, self) => self.indexOf(id) === index);
};

export const RemoveFeatureDialog: FC<{
  open: boolean;
  productConfigurationId: string;
  featureToRemove: {
    _id: string;
    name: string;
    categoryIds: string[];
  };
  allFeatures: Array<{
    _id: string;
    name: string;
    categoryIds: string[];
  }>;
  featuresAdded: string[];
  categorys: CategoryData[];
  groupedOptions: GroupedOptionListItem[];
  onClose?: () => void;
  onRemoveComplete?: () => void;
}> = ({
  open,
  productConfigurationId,
  featureToRemove,
  allFeatures,
  featuresAdded,
  categorys,
  groupedOptions,
  onClose,
  onRemoveComplete,
}) => {
  const theme = useTheme();
  const [isRemoving, setIsRemoving] = useState(false);
  const { featuresInfo } = useProductOptionContext();
  const [removeProductConfigurationListItem] = useMutation(
    REMOVE_PRODUCT_CONFIGURATION_LIST_ITEM,
    {
      update: () => {
        // Don't close dialog here, we'll do it after the mutation completes
      },
    }
  );

  // Find which categories are used by other features
  const categoriesInUse = useMemo(() => {
    const result = new Map<
      string,
      {
        categoryName: string;
        usedByFeatures: Array<{ id: string; name: string }>;
      }
    >();

    expandCategories(featureToRemove.categoryIds, categorys).forEach(
      (categoryId) => {
        const otherAddedFeatures = allFeatures.filter(
          (f) =>
            f._id !== featureToRemove._id &&
            expandCategories(f.categoryIds, categorys).includes(categoryId) &&
            featuresAdded.includes(f._id)
        );

        if (otherAddedFeatures.length > 0) {
          const category = categorys.find((c) => c._id === categoryId);
          if (category) {
            result.set(categoryId, {
              categoryName: category.name,
              usedByFeatures: otherAddedFeatures.map((f) => ({
                id: f._id,
                name: f.name,
              })),
            });
          }
        }
      }
    );

    return result;
  }, [
    JSON.stringify(featureToRemove),
    JSON.stringify(allFeatures),
    JSON.stringify(categorys),
    JSON.stringify(featuresAdded),
  ]);

  // Find all subcategories and their options that need to be removed
  const categoriesToRemove = useMemo(() => {
    const result = new Map<
      string,
      {
        categoryName: string;
        subCategories: Array<{ id: string; name: string }>;
      }
    >();

    // Helper function to get all subcategories for a category
    const getAllSubCategories = (categoryId: string) => {
      const category = categorys.find((c) => c._id === categoryId);
      if (!category) return [];

      // Get direct subcategories
      const directSubCategories = category.SubCategorys.map((sub) => ({
        id: sub._id,
        name: sub.name,
      }));

      // Get subcategories from required categories
      const requiredSubCategories = (category.requiredCategorys || [])
        .filter((reqCatId) => !categoriesInUse.has(reqCatId)) // Only include if not used by other features
        .flatMap((reqCatId) => {
          const reqCategory = categorys.find((c) => c._id === reqCatId);
          return reqCategory
            ? reqCategory.SubCategorys.map((sub) => ({
                id: sub._id,
                name: sub.name,
              }))
            : [];
        });

      return [...directSubCategories, ...requiredSubCategories];
    };
    featureToRemove.categoryIds.forEach((categoryId) => {
      // Only include if not used by other features
      if (!categoriesInUse.has(categoryId)) {
        const category = categorys.find((c) => c._id === categoryId);
        if (category) {
          result.set(categoryId, {
            categoryName: category.name,
            subCategories: getAllSubCategories(categoryId),
          });
        }
      }
    });

    return result;
  }, [
    JSON.stringify(featureToRemove),
    JSON.stringify(categoriesInUse),
    JSON.stringify(groupedOptions),
    JSON.stringify(featuresInfo),
  ]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle
        sx={{
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
        }}
      >
        Remove {featureToRemove.name}
      </DialogTitle>
      <DialogContent>
        {categoriesToRemove.size > 0 && (
          <>
            <Typography variant="subtitle1">
              The following components will be removed:
            </Typography>
            <Box sx={{ ml: 2, mb: 2 }}>
              {Array.from(categoriesToRemove.entries()).map(
                ([categoryId, data]) => (
                  <Box key={categoryId} sx={{ mb: 1 }}>
                    <Typography>• {data.categoryName}:</Typography>
                  </Box>
                )
              )}
            </Box>
          </>
        )}
        {categoriesInUse.size > 0 && (
          <>
            <Typography variant="subtitle1">
              The following components will be kept (used by other features):
            </Typography>
            <Box sx={{ ml: 2 }}>
              {Array.from(categoriesInUse.entries()).map(
                ([categoryId, data]) => (
                  <Box key={categoryId} sx={{ mb: 1 }}>
                    <Typography>
                      • {data.categoryName} (used by{" "}
                      {data.usedByFeatures.map((f) => f.name).join(", ")})
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => {
            setIsRemoving(true);
            // Get all subcategory IDs that need to be removed
            const subCategoryIds = Array.from(
              categoriesToRemove.values()
            ).flatMap((data) => data.subCategories.map((sub) => sub.id));
            removeProductConfigurationListItem({
              variables: {
                subCategoryIds,
                productConfigurationId,
              },
            })
              .then(() => {
                // Call onRemoveComplete first to trigger refetch
                onRemoveComplete && onRemoveComplete();
                // Wait for refetch to complete before closing dialog
                setTimeout(() => {
                  setIsRemoving(false);
                  onClose && onClose();
                }, 1000);
              })
              .catch((error) => {
                console.error("Error removing feature:", error);
                setIsRemoving(false);
              });
          }}
          disabled={isRemoving}
          startIcon={
            isRemoving ? (
              <IcIcon
                icon="line-md:loading-loop"
                style={{
                  color: theme.palette.warning.contrastText,
                  width: "1.5em",
                  height: "1.5em",
                }}
              />
            ) : undefined
          }
        >
          {isRemoving ? "Removing..." : "Remove Feature"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
